import { collection, list, named, text, toggle, url, type Choice } from '../../themes/settingFields';
import { element } from './definition';
import { look, paint, strip, typeface } from './styleTokens';

// The store's menu. `items` holds a one-off strip of links typed here; bind it to a menu instead and
// the links come from Navigation — the one place a merchant makes and edits a menu, which every header
// on every page then reads, exactly as Shopify's header picks a menu by name.
//
// There is no "where do the links come from" setting, because the connector on the links row already is
// that question, asked the same way on every field in the builder that the shop can answer.
//
// Below a width the merchant picks it folds behind a button, into a drawer or a panel, which is the one
// piece of behaviour a row of links cannot get from styles alone — the reason this is an element.

/** Which screens get the menu button — LightFunnels' "use burger menu on", against the engine's widths. */
const FOLDS: readonly Choice[] = [
  ['tablet', 'Tablets and phones'], ['mobile', 'Phones only'], ['always', 'Every screen'], ['never', 'Never — wrap the links']
];

/** The shape the folded list opens in — LightFunnels' "open as", and the drawer every phone menu takes. */
const OPEN_AS: readonly Choice[] = [['drawer', 'A drawer from the side'], ['dropdown', 'A panel under the button']];

/** How a drop-down opens on a desktop. A press always works; this is only whether a hover opens it too. */
const TRIGGERS: readonly Choice[] = [['hover', 'Hover'], ['click', 'Click']];

/** The store's menu: which one, how it folds on a phone, and the custom links a one-off strip carries. */
export const menuElement = element('menu', {
  label: 'Menu',
  description: 'One of your store’s menus in a row, folded behind a menu button on a tablet or a phone.',
  category: 'Other',
  icon: 'menu',
  props: [
    {
      ...list('items', 'Links', [text('label', 'Label'), url('href', 'Link')]),
      help: 'Type them here, or use the connector to draw them from a menu under Online store › Navigation.'
    },
    named('fold', 'Use the menu button on', FOLDS),
    named('openAs', 'The button opens', OPEN_AS),
    named('trigger', 'Drop-down opens on', TRIGGERS),
    text('menuLabel', 'Menu button label'),
    { ...toggle('megaProducts', 'Show products in mega menus'), help: 'Three of your products at the end of every drop-down that has columns, as Sleek’s “Trend this week”.' },
    { ...collection('megaCollection', 'Products from', 'Newest products'), help: 'The collection the mega menu’s products come from.' },
    text('megaTitle', 'Products title'),
    { ...text('ariaLabel', 'Name for screen readers'), help: 'Read out in place of the menu icon, e.g. Main.' }
  ],
  defaults: {
    props: {
      // A fresh menu lands with three real destinations, so an unbound one is never an invisible strip.
      items: [{ label: 'Home', href: '/' }, { label: 'Shop', href: '/collections/all' }, { label: 'Contact', href: '/pages/contact' }],
      fold: 'tablet', openAs: 'drawer', trigger: 'hover', menuLabel: 'Menu', ariaLabel: 'Main', megaTitle: 'Trend this week'
    },
    styles: look({ ...strip('0px'), fontFamily: typeface('body'), fontSize: '16px', fontWeight: 500, color: paint('heading') })
  }
});
